import {Injectable} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';

@Injectable()
export class FormHelperService {
    static clearArray(array: FormArray) {
        if (!array || !array.length || !array.removeAt) {
            return;
        }

        while (array.length) {
            array.removeAt(0);
        }
    }

    /**
     * Обновить и провалидировать поле, сделав его touched
     * @param field
     * @param value
     */
    static updateWithTouch(field: AbstractControl, value?: any) {
        if (value !== undefined) {
            field.patchValue(value);
        }
        field.markAsTouched();
        field.markAsDirty();
        field.updateValueAndValidity();
    }

    static makeClean(field: AbstractControl) {
        field.markAsUntouched();
        field.markAsPristine();
    }

    /**
     * Сбросить значения формы
     * @param formGroup
     * @param makeTouched
     */
    static reset(formGroup: FormGroup, makeTouched = true) {
        Object.keys(formGroup.controls).forEach(name => {
            const control = formGroup.get(name);

            if (control instanceof FormControl) {
                control.patchValue('');
                if (makeTouched) {
                    control.markAsTouched();
                    control.markAsDirty();
                }
                control.updateValueAndValidity();
            }

            if (control instanceof FormArray) {
                while (control.length) {
                    control.removeAt(0);
                }
            }

            if (control instanceof FormGroup) {
                FormHelperService.reset(control);
            }
        });
    }

    /**
     * Получить имя контрола
     */
    static getControlName(control: AbstractControl): string {
        const parent = control.parent;

        if (!parent) {
            return;
        }

        let name = null;

        Object.keys(parent.controls).some(key => {
            if (parent.controls[key] === control) {
                name = key;
                return true;
            }
        });

        return name;
    }

    /**
     * Рекурсивно валидировать все поля переданной формы
     * @param group
     */
    static validateGroup(group: FormGroup) {
        for (const key of Object.keys(group.controls)) {

            try {
                const control = group.get(key);

                if (control instanceof FormGroup) {
                    this.validateGroup(control);
                    continue;
                }

                if (control instanceof FormArray) {
                    const array = control as FormArray;
                    control.markAsDirty();
                    control.markAsTouched();
                    control.updateValueAndValidity();
                    array['controls'].forEach((g: FormGroup) => this.validateGroup(g));
                    continue;
                }

                if (control instanceof FormControl) {
                    control.markAsDirty();
                    control.markAsTouched();
                    control.updateValueAndValidity();
                }
            } catch (e) {
                console.error('Cannot validate control with name=', key);
            }

        }
    }


    /**
     * Восстанавливаем значения формы,
     * рекурсивно проходя по всем полям формы и восстанавливая их в зависимости от того,
     * какого типа поле (массив, группа-объект или значение).
     *
     * ВНИМАНИЕ: Есди поле пришло с backend, но в форме его нет, то оно там создастся, так как это может быть динамическим полем
     * Результирующая форма получается как объединение исходной формы, её вложенных форм и структуры, пришедшей с backend
     */
    static setFormValues(values, group: FormGroup): void {
        if (!values || typeof values !== 'object') {
            return;
        }

        Object.keys(values).forEach(key => {
            if (values[key] instanceof Array) {
                FormHelperService.setArrayValueToGroup(group, values[key], key);
                return;
            }

            if (values[key] instanceof Object) {
                FormHelperService.setGroupValueToGroup(group, values[key], key);
                return;
            }

            FormHelperService.setControlValueToGroup(group, values[key], key);
        });
    }

    /**
     * Just patch formControl with value
     */
    static setControlValueToGroup(group: FormGroup, value: any, name: string) {
        if (!group.contains(name)) {
            group.addControl(name, new FormControl(''));
        }

        const currentValue = group.controls[name].value;

        group.controls[name].patchValue((value === undefined || value === null) ? currentValue : value);
    }

     /**
     * Restore value array as FormArray of FormGroup[]
     */
     static setArrayValueToGroup(group: FormGroup, value: any[], name: string) {
        if (!value || !value.length) {
            /**
             * Если коллекция пустая, то считаем, что это FormArray
             */
            if (!group.contains(name)) {
                group.addControl(name, new FormArray([]));
            }
            return;
        }

        /**
         * If value is array of plain value of Array of plain values, just handle it as simple control value
         * (let's deal that nobody will pass Array of Arrays of Objects)
         */
        if (!(value[0] instanceof Object) || value[0] instanceof Array) {
            FormHelperService.setControlValueToGroup(group, value, name);
            return;
        }

        /**
         * If has not control with this name, added it as FormArray
         */
        if (!group.contains(name)) {
            group.addControl(name, new FormArray([]));
        }

        /**
         * Here we know, that value is Array of Objects, so restore them as Array of FormGroups[]
         */
        const array = <FormArray>group.controls[name];
        value.forEach(object => {
            const newArrayGroup = new FormGroup({});
            FormHelperService.setFormValues(object, newArrayGroup);
            array.push(newArrayGroup);
        });
    }

    /**
     * Restore value as FormGroup inside provided FormGroup
     */
    static setGroupValueToGroup(group: FormGroup, value: any, name: string) {
        if (!group.contains(name)) {
            group.addControl(name, new FormGroup({}));
        }

        const newSubGroup = <FormGroup>group.controls[name];
        FormHelperService.setFormValues(value, newSubGroup);
    }

    /**
     * Добавляем новый динамический блок в место, указанное в field
     * @param {FormGroup} form - Форма, в которой добавляем блок
     * @param {string} group - Группа формы, в которой добавляем блок
     * @param {FormGroup} newBlock - Новый блок для добавления
     * @param event
     */
    addDynamicBlock(form: FormGroup, group: string, newBlock: FormGroup, event: any): void {
        event.preventDefault();
        const control = <FormArray>form.controls[group];
        control.push(newBlock);
    }

    /*
    * Метод создания пустой группы.
    * Используется для создания полей для динамических вложенных форм
    * */
    createEmptyFormGroup() {
        return new FormGroup({});
    }

    /**
     * Удалить динамическое поле
     * @param {FormGroup} form
     * @param {string} field
     * @param {number} index
     * @param event
     */
    removeDynamicField(form: FormGroup, field: string, index: number, event: any): void {
        if (event) {
            event.preventDefault();
        }

        const control = <FormArray>form.controls[field];

        control.removeAt(index);
    }
}
